import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useAppLocale } from '@/components/i18n/locale-provider'
import { IntentPrefetchLink } from '@/components/navigation/intent-prefetch-link'
import { describeFlowCadence } from '@/lib/flow-templates/format-flow-timing'
import type { PublicFlowTemplateCard } from '@/lib/flow-templates/load-public-flow-templates'
import { numberFormat } from '@/lib/i18n/format'
import {
  FLOW_CARD_CLASSES,
  FLOW_CARD_STRIP_CLASSES,
  FLOW_CARD_STRIP_ITEM_CLASSES,
  FLOW_CARD_STRIP_SIZES,
} from '../lib/grid-classes'
import { TemplateBrandLogo } from './template-brand-logo'

const STRIP_SLOTS = [0, 1, 2] as const

/**
 * The first three emails side by side, same height, full opacity. Empty
 * slots keep the strip's rhythm so every card is the same height.
 */
function FlowCardStrip({
  thumbnails,
  brandName,
}: {
  thumbnails: PublicFlowTemplateCard['thumbnails']
  brandName: string
}) {
  const t = useTranslations('flows')
  const locale = useAppLocale()
  return (
    <div className={FLOW_CARD_STRIP_CLASSES}>
      {STRIP_SLOTS.map((slot) => {
        const thumbnail = thumbnails[slot]
        const position = numberFormat(locale).format(slot + 1)
        return (
          <div key={slot} className={FLOW_CARD_STRIP_ITEM_CLASSES}>
            {thumbnail ? (
              <Image
                src={thumbnail.imagePngUrl}
                alt={t('card.thumbnailAlt', { brandName, position })}
                fill
                sizes={FLOW_CARD_STRIP_SIZES}
                quality={60}
                loading="lazy"
                className="object-cover object-top"
              />
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

/**
 * One flow in the gallery: the first three emails side by side, then the
 * brand and what the sequence is — artwork over a caption, the way the
 * template cards beside it read. The whole card is the link — an intent
 * prefetch (pointer or focus) carries the flow's own page, so the click
 * paints the sequence instead of its skeleton. Its copy is the `flows`
 * catalog, the brand logo's alt text the `templates` one.
 */
export function FlowCard({
  card,
  href,
}: {
  card: PublicFlowTemplateCard
  href: string
}) {
  const t = useTranslations('flows')
  const cadence = describeFlowCadence(t, {
    emailCount: card.emailCount,
    spanDays: card.spanDays,
  })
  return (
    <IntentPrefetchLink
      href={href}
      className={FLOW_CARD_CLASSES}
      aria-label={t('card.label', {
        brandName: card.brandName,
        flowType: card.flowType,
        cadence,
      })}
    >
      <FlowCardStrip thumbnails={card.thumbnails} brandName={card.brandName} />
      <div className="mt-3 flex w-full min-w-0 items-center gap-2.5">
        <TemplateBrandLogo
          domain={card.brandDomain}
          name={card.brandName}
          logoUrl={card.logoUrl}
          className="size-7 shrink-0"
        />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-medium text-foreground text-sm">
            {card.brandName}
          </span>
          <span className="truncate text-muted-foreground text-xs">
            {cadence}
          </span>
        </span>
      </div>
    </IntentPrefetchLink>
  )
}
